import { computed, reactive } from "vue";
import { useInputState } from "./use-input-state.js";
import { useFormLogger } from "./use-form-logger.js";
export interface SubmitStateTotals {
  totalRequired: number;
  totalDone: number;
  totalErrors: number;
}
export type SubmitStateTotalsKey = {
  [K in keyof SubmitStateTotals]: K;
}[keyof SubmitStateTotals];

export interface SubmitState extends SubmitStateTotals {
  requiredFields: string[];
  doneFields: string[];
  fieldsWithErrors: string[];
  fieldsWithVisibleErrors: string[];
  totalDoneRequired: boolean;
  submitReadyBool: boolean;
  setAllFieldsTouched: () => void;
}
export type SubmitStateKey = { [K in keyof SubmitState]: K }[keyof SubmitState];

export function useSubmitState<TEnabledFormField extends string>({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger();
  try {
    callerLabel ??= "useSubmitState";
    const { inputStates } = useInputState<TEnabledFormField>({ callerLabel });

    const InputStateKeysRef = computed(() => {
      return Object.keys(inputStates) as TEnabledFormField[];
    });

    const setAllFieldsTouched = () => {
      InputStateKeysRef.value.forEach((key) => {
        inputStates[key].touched = true;
      });
    };

    const requiredFields = computed<TEnabledFormField[]>(() => {
      //return filterByProp(inputStates, InputStateKeysRef.value, "required");
      return InputStateKeysRef.value.filter((k) => inputStates[k].required);
    });
    const doneFields = computed<TEnabledFormField[]>(() => {
      //return filterByProp(inputStates, InputStateKeysRef.value, "done");
      return InputStateKeysRef.value.filter((k) => inputStates[k].done);
    });

    const fieldsWithErrors = computed<TEnabledFormField[]>(() => {
      //return filterByProp(inputStates, InputStateKeysRef.value, "hasError");
      return InputStateKeysRef.value.filter((k) => inputStates[k].hasError);
    });

    const fieldsWithVisibleErrors = computed<TEnabledFormField[]>(() => {
      //return filterByProp(inputStates, InputStateKeysRef.value, "showError");
      return InputStateKeysRef.value.filter((k) => inputStates[k].showError);
    });

    const totalRequired = computed(() => {
      return requiredFields.value.length;
    });
    const totalDone = computed(() => {
      return doneFields.value.length;
    });
    const totalErrors = computed(() => {
      return fieldsWithErrors.value.length;
    });
    const totalDoneRequired = computed(() => {
      return totalDone.value >= totalRequired.value;
    });
    const submitReadyBool = computed(() => {
      const done = totalDoneRequired.value;
      const noErrors = totalErrors.value === 0;
      const isReady = done && noErrors;
      if (!isReady) {
        logger.warn("error detected in submitState");
      }
      return isReady;
    });

    return {
      submitState: reactive({
        requiredFields,
        doneFields,
        fieldsWithErrors,
        fieldsWithVisibleErrors,
        totalRequired,
        totalDone,
        totalErrors,
        totalDoneRequired,
        submitReadyBool,
        setAllFieldsTouched,
      }),
    };
  } catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? "unknown"}`);
    throw err;
  }
}
